import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useCart } from '../context/CartContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentResultPage = ({ outcome }) => {
  const [params] = useSearchParams();
  const orderId = params.get('order_id') || '';
  const { getCartCount } = useCart();
  const [paymentStatus, setPaymentStatus] = useState('checking');
  const [order, setOrder] = useState(null);

  useEffect(() => {
    if (!orderId) return;
    let attempts = 0;
    let cancelled = false;

    const poll = async () => {
      attempts += 1;
      try {
        // Use logged-in user's email if available, else read from localStorage cart hint
        const storedUser = JSON.parse(localStorage.getItem('user') || 'null');
        const email = storedUser?.email || '';
        const url = `${API_URL}/api/payment/status/${encodeURIComponent(orderId)}?email=${encodeURIComponent(email)}`;
        const res = await fetch(url);
        if (res.ok) {
          const data = await res.json();
          if (cancelled) return;
          setOrder(data);
          if (data.payment_status === 'paid') {
            setPaymentStatus('paid');
            return;
          }
          if (data.payment_status === 'failed') {
            setPaymentStatus('failed');
            return;
          }
        }
      } catch (_) {}

      if (attempts >= 8) {
        // Couldn't confirm via callback — fall back to URL-based outcome
        setPaymentStatus(outcome);
        return;
      }
      setTimeout(poll, 1500);
    };
    poll();
    return () => { cancelled = true; };
  }, [orderId, outcome]);

  const isPaid = paymentStatus === 'paid' || (paymentStatus === 'success' && outcome === 'success');
  const isFailed = paymentStatus === 'failed' || outcome === 'fail';

  return (
    <div className="min-h-screen bg-gray-50">
      <Header cartCount={getCartCount()} />
      <main className="max-w-2xl mx-auto px-4 py-16 text-center">
        {paymentStatus === 'checking' && (
          <div className="bg-white rounded-xl p-10 shadow-sm">
            <Loader2 size={48} className="mx-auto animate-spin text-red-600 mb-4" />
            <h1 className="text-2xl font-bold text-gray-800 mb-2">Ödemeniz doğrulanıyor</h1>
            <p className="text-gray-600">PayTR'den onay bekleniyor, lütfen bekleyin...</p>
          </div>
        )}

        {paymentStatus !== 'checking' && isPaid && (
          <div className="bg-white rounded-xl p-10 shadow-sm">
            <CheckCircle2 size={64} className="mx-auto text-emerald-500 mb-4" data-testid="payment-success-icon" />
            <h1 className="text-3xl font-bold text-gray-800 mb-2">Ödeme Başarılı!</h1>
            <p className="text-gray-600 mb-2">Siparişiniz alındı. Onay e-postası gönderildi.</p>
            {orderId && (
              <p className="text-sm text-gray-500 mb-6">
                Sipariş No: <span className="font-mono font-semibold">{orderId}</span>
              </p>
            )}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to={`/siparis-takip?order_id=${encodeURIComponent(orderId)}`}
                className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold"
              >
                Siparişimi Takip Et
              </Link>
              <Link to="/" className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold">
                Ana Sayfa
              </Link>
            </div>
          </div>
        )}

        {paymentStatus !== 'checking' && isFailed && (
          <div className="bg-white rounded-xl p-10 shadow-sm">
            <XCircle size={64} className="mx-auto text-red-500 mb-4" data-testid="payment-fail-icon" />
            <h1 className="text-3xl font-bold text-gray-800 mb-2">Ödeme Başarısız</h1>
            <p className="text-gray-600 mb-6">
              Ödemeniz alınamadı. Bakiye, limit veya kart bilgilerinizi kontrol edip tekrar deneyebilirsiniz.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to={`/payment/${encodeURIComponent(orderId)}`}
                className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold"
              >
                Tekrar Dene
              </Link>
              <Link to="/cart" className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold">
                Sepete Dön
              </Link>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default PaymentResultPage;
